// Copyright (c) 2021 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, filter, mergeMap } from "rxjs/operators"
import { StatusCode } from "grpc-web"
import * as Actions from "../actions"
import * as TimelineSlice from "../slices/timeline"
import {
  TrashStatus,
  UnTrashTimeRowsRequest,
  UnTrashTimeRowsResponse
} from "../../../generated/wisetime/user/timeline/v4/timeline_pb"
import { timelineClient } from "app/rpc/client"
import { grpc } from "../../../app/rpc/executor"
import * as TrashedTimeRowSlice from "../trash/slice"
import { isGrpcError } from "../../../lib/rpcutils"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"

type UntrashTimeRowsRpc = (request: UnTrashTimeRowsRequest) => Observable<UnTrashTimeRowsResponse>

export const untrashTimeRows = (untrashTimeRowsRpc: UntrashTimeRowsRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.untrashTimeRows.match),
      mergeMap(action => {
        const timeRowIdsList = action.payload.getTimeRowIdsList()
        return concat(
          of(
            // mark rows as being untrashed
            TimelineSlice.setUpdatingTimeRowsStatus({
              trashStatus: TrashStatus.NOT_TRASHED,
              timeRowIds: timeRowIdsList,
            })
          ),
          of(action.payload).pipe(
            mergeMap(untrashTimeRowsRpc),
            mergeMap(_ => concat(
              // unset trashed status for rows
              of(TimelineSlice.updateTimeRowsStatus({
                trashStatus: TrashStatus.NOT_TRASHED,
                timeRowIds: timeRowIdsList,
              })),
              // remove rows from trash page action pane
              of(TrashedTimeRowSlice.removeActiveTrashedTimeRowIds(timeRowIdsList)),
              // remove pending untrashing
              of(TimelineSlice.removeUpdatingTimeRowsStatus(timeRowIdsList)))
            ),
            catchError(e => concat(
              // unmark rows as being untrashed
              of(TimelineSlice.removeUpdatingTimeRowsStatus(timeRowIdsList)),
              showErrorNotification(e)
            )),
          ),
        )
      }),
    )

const showErrorNotification = (e: unknown): Observable<Action> => {
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.PERMISSION_DENIED) {
      return of(showSnackBar({
        message: t({ id: "msg_untrash_time_rows_failed_permission_denied", message: "Failed to restore time rows. You do not have permission." }),
        messageType: "ERROR",
      }))
    }
    if (code === StatusCode.FAILED_PRECONDITION) {
      return concat(
        of(showSnackBar({
          message: t({ id: "msg_untrash_time_rows_failed_precondition", message: "Failed to restore time rows. Time rows may be out of sync." }),
          messageType: "ERROR",
        })),
        of(TrashedTimeRowSlice.setTrashedRowsRefreshRequired(true))
      )
    }
  }
  return of(showSnackBar({
    message: t({ id: "msg_untrash_time_rows_failed", message: "Failed to restore time rows. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
}

const untrashTimeRowsRpc = (request: UnTrashTimeRowsRequest): Observable<UnTrashTimeRowsResponse> =>
  grpc(() => timelineClient.unTrashTimeRows(request, null))

export default untrashTimeRows(untrashTimeRowsRpc)
